import React, { useState, useRef, useEffect } from 'react';
import { Printer, Download, ArrowLeft } from 'lucide-react';
import logo from '../assets/infraLogo.jpg';

const EnquiryQuotationForm = () => {
  const printRef = useRef();
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchaseItemData, setPurchaseItemData] = useState({
    company: {},
    enquiry: {},
    items: [],
  });
  const [error, setError] = useState(null);

  // Get ID from query string
  const queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id") || "demo-123";

  // Fallback enquiry data
  const enquiryData = {
    company: {
      name: "NISSI INFOTECH",
      tagline: "CIVIL CONSTRUCTION",
      address: "3/42-12, Nagappa Colony, Kavundampalayam, Salem-636030, Tamil Nadu, India",
      phone: "Phone No. 9003744644",
      email: "infraconsoft.implementation@nissiinfotech.in"
    },
    enquiry: {
      enquiryNo: "",
      date: "",
      to: "",
      for: "Nissi Infotech",
      pinCode: ""
    },
    items: [],
    note: "Please provide your best quotation with delivery terms and payment conditions.",
    authorizedSignatory: "Manager - Purchase"
  };

  const handlePrint = () => window.print();
  const handleDownload = () => console.log("Download functionality would be implemented here");
  const handleBack = () => console.log("Navigate back");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const response = await fetch(`https://www.nissi-india.com/api/purchase_reqPrint2/${id}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const apiData = await response.json();
        console.log("API Response:", apiData);

        if (apiData.success && apiData.data && apiData.data.length > 0) {
          const apiItems = apiData.data.map((item, idx) => ({
            sno: idx + 1,
            itemCode: item.itemcode,
            description: item.itemname,
            partNo: item.itemPartno || "-",
            specification: item.fld_specification || "-",
            qtyReqd: item.fld_qty,
            uom: item.itemuom,
          }));

          const transformedData = {
            ...enquiryData,
            items: apiItems,
          };

          setPurchaseItemData(transformedData);
          setError(null);
        } else {
          throw new Error("No data found");
        }
      } catch (err) {
        console.error("Error fetching purchase data:", err);
        setError(err.message);
        setPurchaseItemData(enquiryData); // fallback
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <div className="p-4 text-center">Loading...</div>;
  if (error) return <div className="p-4 text-center text-red-600">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Controls - Hidden in print */}
      <div className="print:hidden bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ArrowLeft size={18} />
              Back
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className="px-3 py-2 text-purple-600 hover:bg-purple-50 rounded-md transition-colors text-sm"
              >
                {isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded-md transition-colors text-sm"
              >
                <Download size={16} />
                Download PDF
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white hover:bg-purple-700 rounded-md transition-colors text-sm"
              >
                <Printer size={16} />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Content */}
      <div className="max-w-4xl mx-auto p-4 print:p-0 print:max-w-none">
        <div
          ref={printRef}
          className="print-container bg-white shadow-lg print:shadow-none border print:border-0 min-h-[297mm]"
          style={{ fontFamily: 'Times New Roman, Times, serif' }}
        >
          {/* Company Header */}
          <div className="print-header px-6 py-4 border-b-2 border-black">
            <div className="flex items-start justify-between print:justify-center">
              {/* Logo */}
              <div className="flex-shrink-0 mr-6">
                <div className="w-20 h-20 border-2 border-gray-400 flex items-center justify-center bg-gray-50">
                  <img src={logo} alt="Company Logo" className="w-full h-full object-contain" />
                </div>
              </div>
              {/* Info */}
              <div className="flex-1 text-center">
                <h1 className="text-2xl font-bold mb-1">{purchaseItemData.company.name}</h1>
                <p className="text-lg font-semibold text-gray-700 mb-2">
                  {purchaseItemData.company.tagline}
                </p>
                <div className="text-sm space-y-1">
                  <p className="font-medium">{purchaseItemData.company.address}</p>
                  <div className="flex justify-center gap-8 text-sm">
                    <span>{purchaseItemData.company.phone}</span>
                    <span>{purchaseItemData.company.email}</span>
                  </div>
                </div>
              </div>
              <div className="w-20 print:hidden"></div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-6">
            <table className="w-full border-collapse border border-black text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black p-2">S.No.</th>
                  <th className="border border-black p-2">Item Code</th>
                  <th className="border border-black p-2">Description</th>
                  <th className="border border-black p-2">Part No</th>
                  <th className="border border-black p-2">Specification</th>
                  <th className="border border-black p-2">Qty.Reqd.</th>
                </tr>
              </thead>
              <tbody>
                {purchaseItemData.items.length > 0 ? (
                  purchaseItemData.items.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-black p-2 text-center">{item.sno}</td>
                      <td className="border border-black p-2 text-center">{item.itemCode}</td>
                      <td className="border border-black p-2">{item.description}</td>
                      <td className="border border-black p-2 text-center">{item.partNo}</td>
                      <td className="border border-black p-2">{item.specification}</td>
                      <td className="border border-black p-2 text-center">{item.qtyReqd}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center p-4">
                      No items found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

       
  


              {/* Bottom Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:gap-4 mb-6 print:mb-4">
                {/* Note Section */}
                <div className="note-section">
                  <h3 className="text-lg print:text-xl font-bold mb-3 print:mb-2">NOTE:</h3>
                  <div className="border border-black p-4 print:p-3 min-h-32 print:min-h-24">
                    <div className="text-sm print:text-base whitespace-pre-wrap">
                      {enquiryData.note || "Please provide delivery terms, payment conditions, and validity period."}
                    </div>
                  </div>
                </div>

                {/* Right Side - For & Signature */}
                <div className="space-y-4 print:space-y-3">
                  {/* For Section */}
                  <div>
                    <h3 className="text-lg print:text-xl font-bold mb-2">FOR:</h3>
                    <div className="border border-black p-3 print:p-2 text-center font-semibold text-base print:text-lg">
                      {enquiryData.enquiry.for}
                    </div>
                  </div>

                  {/* Signature Section */}
                  <div className="signature-section">
                    <h3 className="text-lg print:text-xl font-bold mb-2">AUTHORISED SIGNATORY:</h3>
                    <div className="signature-box border border-black p-4 print:p-3 min-h-20 print:min-h-16">
                      <div className="h-full flex flex-col justify-between">
                        <div className="flex-1"></div>
                        <div className="border-t border-black pt-2 text-center text-sm print:text-base">
                          {enquiryData.authorizedSignatory}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 print:mt-6 pt-4 border-t border-gray-300 text-center">
                <p className="text-xs print:text-sm text-gray-600 mb-1">
                  Document Generated by Infraconsoft - www.infraconsoft.com
                </p>
                <p className="text-xs text-gray-500">
                  Generated on: {new Date().toLocaleDateString('en-IN')} | Page 1 of 1
                </p>
              </div>
            </div>
          </div>
        </div>
    
  
  );
};

export default EnquiryQuotationForm;